import { geoBounds, geoMercator } from 'd3-geo';

import { GeolocationArea, GeolocationBoundaries } from '../../../domains/Geolocation/Geolocation';

import type { BBox, WorldCountry } from '../../../shared/geo/geoAsset';
import type { GeoProjection } from 'd3-geo';
import type { MultiPoint, MultiPolygon, Polygon, Position } from 'geojson';

export type RegionGeometry = Polygon | MultiPolygon;

type ApiRing = Array<{ longitude: number; latitude: number }>;

const MIN_HALF_SPAN_DEG = 0.01;

function ringToPositions(ring: ApiRing): Position[] {
    return ring.map(({ longitude, latitude }) => {
        return [longitude, latitude];
    });
}

function signedArea(ring: Position[]): number {
    const { length } = ring;
    let area = 0;
    for (let index = 0; index < length; index++) {
        const [x1, y1] = ring[index];
        const [x2, y2] = ring[(index + 1) % length];
        area += x1 * y2 - x2 * y1;
    }

    return area / 2;
}

// d3-geo interprets a polygon's inside from its winding order: exterior rings
// must be clockwise (interior rings counter-clockwise). A counter-clockwise
// exterior (the GeoJSON RFC 7946 convention) is read as "the whole sphere minus
// the shape", which Mercator projects through the poles into NaN coordinates.
// PWAPI boundaries winding is not guaranteed, so we normalize every ring here.
function rewindRing(ring: Position[], clockwise: boolean): Position[] {
    const isClockwise = signedArea(ring) < 0;

    return isClockwise === clockwise ? ring : [...ring].reverse();
}

function rewindRings(rings: ApiRing[]): Position[][] {
    return rings.map((ring, index) => {
        return rewindRing(ringToPositions(ring), index === 0);
    });
}

export function boundariesToGeometry(boundaries: GeolocationBoundaries): RegionGeometry {
    if (boundaries.type === 'Polygon') {
        return {
            type: 'Polygon',
            coordinates: rewindRings(boundaries.coordinates),
        };
    }

    return {
        type: 'MultiPolygon',
        coordinates: boundaries.coordinates.map(rewindRings),
    };
}

export function geometryBounds(geometry: RegionGeometry): BBox {
    const [[west, south], [east, north]] = geoBounds(geometry);

    return [west, south, east, north];
}

// The axis-aligned extent covering the whole destination, used when the map should frame a
// country rather than a single region.
//
// The API's corner labels are unreliable — prod boundingBoxes come back with south/north
// (and sometimes east/west) swapped between corners — so derive the extent from the min/max
// of all four corners rather than trusting any single label. Returns null for a box that
// straddles the antimeridian (span > 180°): the naive min/max rectangle would then wrap
// almost the whole globe and project to NaN, so the caller falls back to region framing.
export function boundingBoxToBounds(boundingBox: GeolocationArea['boundingBox']): BBox | null {
    const corners = [
        boundingBox.southWest,
        boundingBox.southEast,
        boundingBox.northEast,
        boundingBox.northWest,
    ];
    const longitudes = corners.map((corner) => corner.longitude);
    const latitudes = corners.map((corner) => corner.latitude);
    const west = Math.min(...longitudes);
    const east = Math.max(...longitudes);
    const south = Math.min(...latitudes);
    const north = Math.max(...latitudes);

    if (east - west > 180) {
        return null;
    }

    return [west, south, east, north];
}

export function buildRegionProjection({
    bounds,
    width,
    height = width,
    paddingRatio,
}: {
    bounds: BBox;
    width: number;
    height?: number;
    paddingRatio: number;
}): GeoProjection {
    const [west, south, east, north] = bounds;

    const centerLng = (west + east) / 2;
    const centerLat = (south + north) / 2;
    const halfSpanLng = ((east - west) * (1 + paddingRatio)) / 2 || MIN_HALF_SPAN_DEG;
    const halfSpanLat = ((north - south) * (1 + paddingRatio)) / 2 || MIN_HALF_SPAN_DEG;

    const fitObject: MultiPoint = {
        type: 'MultiPoint',
        coordinates: [
            [centerLng - halfSpanLng, centerLat - halfSpanLat],
            [centerLng + halfSpanLng, centerLat + halfSpanLat],
        ],
    };

    // fitExtent scales uniformly to fit the padded region box within the viewport and
    // centers it, so a non-square viewport letterboxes extra sea/land around the region
    // rather than cropping it.
    return geoMercator().fitExtent(
        [
            [0, 0],
            [width, height],
        ],
        fitObject,
    );
}

// The exact geographic window a projection shows, obtained by inverting the viewport corners.
// Both framing modes cull neighbours/lakes against this — what the viewport actually reveals,
// letterboxed sea/land included — so the projection is the single source of truth for framing.
export function computeVisibleWindow(
    projection: GeoProjection,
    { width, height }: { width: number; height: number },
): BBox {
    const topLeft = projection.invert?.([0, 0]);
    const bottomRight = projection.invert?.([width, height]);

    // Mercator always inverts within its valid range; this guards the GeoProjection type.
    if (!topLeft || !bottomRight) {
        return [-180, -85, 180, 85];
    }

    const [west, north] = topLeft;
    const [east, south] = bottomRight;

    return [west, south, east, north];
}

export function bboxIntersects(a: BBox, b: BBox): boolean {
    return a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];
}

export function ringsToMultiPolygon(rings: Position[][]): MultiPolygon {
    return {
        type: 'MultiPolygon',
        coordinates: rings.map((ring) => {
            return [rewindRing(ring, true)];
        }),
    };
}

function ringCentroid(ring: Position[]): Position {
    const { length } = ring;
    let areaSum = 0;
    let centroidX = 0;
    let centroidY = 0;
    for (let index = 0; index < length; index++) {
        const [x1, y1] = ring[index];
        const [x2, y2] = ring[(index + 1) % length];
        const cross = x1 * y2 - x2 * y1;
        areaSum += cross;
        centroidX += (x1 + x2) * cross;
        centroidY += (y1 + y2) * cross;
    }

    if (areaSum === 0) {
        return ring[0];
    }

    return [centroidX / (3 * areaSum), centroidY / (3 * areaSum)];
}

function largestExteriorRing(geometry: RegionGeometry): Position[] {
    const exteriorRings =
        geometry.type === 'Polygon'
            ? [geometry.coordinates[0]]
            : geometry.coordinates.map((polygon) => {
                  return polygon[0];
              });

    return exteriorRings.reduce((largest, ring) => {
        return Math.abs(signedArea(ring)) > Math.abs(signedArea(largest)) ? ring : largest;
    });
}

// Even-odd ray casting across every ring of a country outline.
function pointInRings([lng, lat]: Position, rings: Position[][]): boolean {
    let inside = false;
    for (const ring of rings) {
        const { length } = ring;
        for (let index = 0, previous = length - 1; index < length; previous = index++) {
            const [currentLng, currentLat] = ring[index];
            const [previousLng, previousLat] = ring[previous];
            const crossesLatitude = currentLat > lat !== previousLat > lat;
            const intersectionLng =
                ((previousLng - currentLng) * (lat - currentLat)) / (previousLat - currentLat) +
                currentLng;
            if (crossesLatitude && lng < intersectionLng) {
                inside = !inside;
            }
        }
    }

    return inside;
}

function bboxContains(bbox: BBox, [lng, lat]: Position): boolean {
    return lng >= bbox[0] && lng <= bbox[2] && lat >= bbox[1] && lat <= bbox[3];
}

function bboxOverlapArea(a: BBox, b: BBox): number {
    const overlapWidth = Math.min(a[2], b[2]) - Math.max(a[0], b[0]);
    const overlapHeight = Math.min(a[3], b[3]) - Math.max(a[1], b[1]);

    return overlapWidth > 0 && overlapHeight > 0 ? overlapWidth * overlapHeight : 0;
}

// Identifies the country a region belongs to, so callers never have to source an
// ISO code. The country whose outline contains the region's main-ring centroid
// wins; a centroid falling in the sea (archipelagos, simplified coastlines) falls
// back to the largest bbox overlap.
export function findRegionCountry(
    countries: WorldCountry[],
    geometry: RegionGeometry,
): WorldCountry | undefined {
    const point = ringCentroid(largestExteriorRing(geometry));
    const containing = countries.find((country) => {
        return bboxContains(country.bbox, point) && pointInRings(point, country.rings);
    });
    if (containing !== undefined) {
        return containing;
    }

    const [[west, south], [east, north]] = geoBounds(geometry);
    const regionBBox: BBox = [west, south, east, north];

    return countries.reduce<WorldCountry | undefined>((best, country) => {
        const bestArea = best === undefined ? 0 : bboxOverlapArea(best.bbox, regionBBox);

        return bboxOverlapArea(country.bbox, regionBBox) > bestArea ? country : best;
    }, undefined);
}
